import HBIcon from "@/components/HBIcon";
import HBSwiper from "@/components/HBSwiper";
import Image from "@/components/Image";
import { getContentType } from "@/utils/getContentType";
import { usePin } from "@/utils/hooks/usePin";
import { gdImgSrcPolicy, getImageSrc2x } from "@/utils/imageUrl";
import classNames from "classnames";
import Link from "next/link";
import { useRouter } from "next/router";
import { stringify } from "qs";
import { useMemo } from "react";
import styles from "./index.module.less";

interface RecommendMaterialProps {
  searchWord: string;
  titleType: "image" | "muti";
  titleImage?: string;
  iconImage?: string;
  title?: string;
  subTitle?: string;
  recommendMaterial?: IPin[];
}

/**搜索推荐主题组件 */
export default function RecommendMaterial({
  searchWord,
  recommend,
  onClose,
}: {
  searchWord: string;
  recommend: RecommendMaterialProps;
  onClose: () => void;
}) {
  const {
    titleType,
    titleImage,
    iconImage,
    title,
    subTitle = "高质量素材，商用 0 风险",
    recommendMaterial = [],
  } = recommend || {};

  const searchTitle = title || searchWord;

  const { onPinClick } = usePin();

  const recommendList = useMemo(() => {
    // 过滤超长图
    return recommendMaterial.filter((pin) => {
      const { width, height } = pin.file;
      return height / width < 2.2;
    });
  }, [recommendMaterial]);

  // 是否是竖图
  const isVertical = useMemo(() => {
    // https://www.figma.com/file/en7OdLzysCCOHgeUQOC6I5/%E8%8A%B1%E7%93%A32024?node-id=429%3A50830&mode=dev
    // 竖图量多于横图
    if (recommendList.length > 0) {
      const matchPicNum = recommendList.reduce((acc, cur) => {
        const { width, height } = cur.file;
        if (width < height) {
          return acc + 1;
        }

        return acc;
      }, 0);

      return matchPicNum > recommendList.length / 2;
    }
  }, [recommendList]);

  const getDisplayWidth = (pin: IPin) => {
    const { width, height } = pin.file;

    if (isVertical) {
      return (width / height) * 215;
    } else {
      return (width / height) * 160;
    }
  };

  const router = useRouter();

  return (
    <div className={styles.recommend} data-content-source="推荐素材">
      <div className={classNames(styles.header, "calculateWrapper", "wrapper")}>
        {titleType === "image" ? (
          <Image
            {...gdImgSrcPolicy(titleImage, { displayHeight: 36 })}
            alt={searchTitle}
            height={36}
          />
        ) : (
          <div className={styles.left}>
            {iconImage && (
              <Image
                {...gdImgSrcPolicy(iconImage, { displayHeight: 36 })}
                alt={searchTitle}
                height={36}
              />
            )}
            {searchTitle && <span className={styles.title}>{searchTitle}</span>}
            {subTitle && <span className={styles.subTitle}>{subTitle}</span>}
          </div>
        )}
        <Link
          href={`/search?${stringify({
            ...router.query,
            type: "material",
          })}`}
        >
          <a
            className={styles.more}
            data-gd-click="button_click"
            data-button-name="更多"
          >
            查看更多
            <HBIcon type="ic_chevron_right" />
          </a>
        </Link>

        <HBIcon
          type="ic_close"
          className={styles.close}
          onClick={onClose}
          data-gd-click="button_click"
          data-button-name="关闭推荐素材"
        />
      </div>
      <div
        className={classNames(
          "calculateWrapper",
          "wrapper",
          styles.recommendMaterial,
          {
            [styles.vertical]: isVertical,
          }
        )}
      >
        <HBSwiper className={styles.swipper}>
          {recommendList.map((pin) => (
            <a
              key={pin.pin_id}
              href={`/pins/${pin.pin_id}${stringify(
                { searchWord },
                { addQueryPrefix: true }
              )}`}
              target="_blank"
              data-gd-expose="content_expose"
              data-gd-click="material_click"
              data-content-id={pin?.pin_id}
              data-unique-id={pin.pin_id}
              data-material-id={pin.file_material?.material_id}
              data-board-id={pin.board_id}
              data-file-id={pin.file_id}
              data-content-type={getContentType(pin)}
              onClick={(e) => {
                onPinClick({
                  pin,
                  event: e,
                  searchWord,
                  currentSrc: (e.target as HTMLImageElement).currentSrc,
                });
              }}
            >
              <Image
                title={pin.raw_text}
                alt={pin.raw_text}
                {...getImageSrc2x(pin.file, {
                  width: getDisplayWidth(pin),
                })}
              />
            </a>
          ))}
        </HBSwiper>
      </div>
    </div>
  );
}
